import React, { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CommunityVisibilitySettings,
  GradingAnswerSnapshot,
  GradingResultSnapshot,
  MarketProfileState,
  PositionFormData,
  UserProfileFormData,
} from './marketProfileStore';
import {
  computeProgress,
  isQuestionnaireLocked,
  isQuestionnaireSubmitted,
  loadMarketProfileForUser,
  persistMarketProfile,
  reconcileMarketProfileWithAuth,
  saveMarketProfile,
  seedMarketProfileIfNeeded,
  stampMarketProfileOwner,
} from './marketProfileStore';
import { fetchRemoteMarketProfile } from '../../../services/adminCommunityService';
import { useAuth } from '../../../contexts/AuthContext';
import { useCommunityDevMode } from '../CommunityDevModeContext';
import { submitPositionWithProfileContext } from './positionProfileContext';
import CombinedShareQuestionnaire from './CombinedShareQuestionnaire';
import ProfileOverview from './ProfileOverview';
import ProfileScreen from './ProfileScreen';
import PositionQuestionnaire from './PositionQuestionnaire';
import GradingQuestionnaire from './GradingQuestionnaire';
import QuestionnairePendingReview from './QuestionnairePendingReview';
import MarketDataTab from './marketData/MarketDataTab';

type Screen =
  | 'overview'
  | 'profile'
  | 'position'
  | 'grading'
  | 'share'
  | 'marketData'
  | 'positionPending'
  | 'gradingPending';

type EntryScreen = 'overview' | 'position' | 'grading' | 'marketData' | 'share';

type Props = {
  onChanged?: () => void;
  entryScreen?: EntryScreen;
};

function resolveScreenForState(state: MarketProfileState): Screen {
  const progress = computeProgress(state);
  if (progress.unlocked) return 'marketData';
  if (state.position.status === 'pending_review' && state.grading.status !== 'pending_review') {
    return 'positionPending';
  }
  if (state.grading.status === 'pending_review' && state.position.status !== 'pending_review') {
    return 'gradingPending';
  }
  return 'overview';
}

function isQuestionnaireScreen(screen: Screen): screen is 'position' | 'grading' | 'share' {
  return screen === 'position' || screen === 'grading' || screen === 'share';
}

function screenAllowed(
  target: Screen,
  positionLocked: boolean,
  gradingLocked: boolean,
): boolean {
  if (target === 'position' && positionLocked) return false;
  if (target === 'grading' && gradingLocked) return false;
  if (target === 'share' && (positionLocked || gradingLocked)) return false;
  return true;
}

function resolveRequestedScreen(
  requested: EntryScreen,
  positionLocked: boolean,
  gradingLocked: boolean,
): Screen {
  if (requested === 'share') {
    if (!positionLocked && !gradingLocked) return 'share';
    if (!positionLocked) return 'position';
    if (!gradingLocked) return 'grading';
    return 'overview';
  }
  if (requested !== 'overview' && screenAllowed(requested, positionLocked, gradingLocked)) {
    return requested;
  }
  return 'overview';
}

export default function MarketProfileTab({ onChanged, entryScreen = 'overview' }: Props) {
  const { user } = useAuth();
  const userEmail = user?.email ?? null;
  const userId = user?.id ?? null;
  const { marketProfileRevision } = useCommunityDevMode();
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const [state, setState] = useState<MarketProfileState>(() =>
    loadMarketProfileForUser(userEmail, userId),
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const [screen, setScreen] = useState<Screen>(() => {
    const initial = loadMarketProfileForUser(userEmail, userId);
    const initialPositionLocked = isQuestionnaireLocked(initial.position.status);
    const initialGradingLocked = isQuestionnaireLocked(initial.grading.status);
    if (entryScreen !== 'overview') {
      const requested = resolveRequestedScreen(entryScreen, initialPositionLocked, initialGradingLocked);
      if (requested !== 'overview') return requested;
    }
    return resolveScreenForState(initial);
  });

  const positionLocked = isQuestionnaireLocked(state.position.status);
  const gradingLocked = isQuestionnaireLocked(state.grading.status);

  const navigateTo = useCallback((target: Screen) => {
    if (!screenAllowed(target, positionLocked, gradingLocked)) return;
    setScreen(target);
  }, [positionLocked, gradingLocked]);

  useEffect(() => {
    if (entryScreen === 'overview') {
      setScreen('overview');
      return;
    }
    setScreen(resolveRequestedScreen(entryScreen, positionLocked, gradingLocked));
  }, [entryScreen, positionLocked, gradingLocked]);

  useEffect(() => {
    if (isQuestionnaireScreen(screen) && !screenAllowed(screen, positionLocked, gradingLocked)) {
      setScreen('overview');
    }
  }, [screen, positionLocked, gradingLocked]);

  useEffect(() => {
    if (marketProfileRevision === 0) return;
    const next = loadMarketProfileForUser(userEmail, userId);
    setState(next);
    setScreen(resolveScreenForState(next));
    onChangedRef.current?.();
  }, [marketProfileRevision, userEmail, userId]);

  useEffect(() => {
    const seeded = seedMarketProfileIfNeeded(userEmail);
    const next = seeded
      ? reconcileMarketProfileWithAuth(seeded, userId, null, userEmail)
      : loadMarketProfileForUser(userEmail, userId);
    setState(next);
    if (seeded && computeProgress(next).unlocked) {
      setScreen('marketData');
    }
    if (seeded) onChangedRef.current?.();
  }, [userEmail, userId]);

  const persist = useCallback(async (build: (prev: MarketProfileState) => MarketProfileState) => {
    let nextState!: MarketProfileState;
    setState((prev) => {
      nextState = build(prev);
      return nextState;
    });
    try {
      const saved = await persistMarketProfile(nextState, userEmail, userId);
      setState(saved);
      setSaveError(null);
      onChangedRef.current?.();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Не удалось сохранить данные');
    }
  }, [userEmail, userId]);

  useEffect(() => {
    let cancelled = false;
    const pullRemote = async () => {
      try {
        const remote = await fetchRemoteMarketProfile();
        if (cancelled) return;
        if (!remote) return;
        setState((prev) => {
          const merged = reconcileMarketProfileWithAuth(prev, userId, remote, userEmail);
          saveMarketProfile(merged, userEmail, userId);
          const progress = computeProgress(merged);
          if (
            (prev.position.status === 'pending_review' && merged.position.status === 'complete')
            || (prev.grading.status === 'pending_review' && merged.grading.status === 'complete')
          ) {
            setScreen(progress.unlocked ? 'marketData' : 'overview');
          }
          return merged;
        });
      } catch {
        /* offline / API недоступен */
      }
    };
    void pullRemote();
    const hasPending =
      state.position.status === 'pending_review'
      || state.grading.status === 'pending_review';
    if (!hasPending) return () => { cancelled = true; };
    const id = window.setInterval(pullRemote, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [userEmail, userId, state.position.status, state.grading.status]);

  useEffect(() => {
    if (!userId) return;
    setState((prev) => stampMarketProfileOwner(prev, userId));
  }, [userId]);

  const progress = computeProgress(state);

  const completePosition = (data: PositionFormData) => {
    void (async () => {
      const enriched = await submitPositionWithProfileContext(data, state.userProfile.data);
      const ts = new Date().toISOString();
      await persist((prev) => ({
        ...prev,
        position: {
          status: 'pending_review',
          data: enriched,
          updatedAt: ts,
          reviewSubmittedAt: ts,
        },
        confirmedAt: null,
      }));
      setScreen('positionPending');
    })();
  };

  const completeShare = (
    data: PositionFormData,
    result: GradingResultSnapshot,
    answers: GradingAnswerSnapshot[],
  ) => {
    void (async () => {
      const enriched = await submitPositionWithProfileContext(data, state.userProfile.data);
      const ts = new Date().toISOString();
      await persist((prev) => ({
        ...prev,
        position: {
          status: 'pending_review',
          data: enriched,
          updatedAt: ts,
          reviewSubmittedAt: ts,
        },
        grading: {
          status: 'pending_review',
          result,
          answers,
          updatedAt: ts,
          reviewSubmittedAt: ts,
        },
        confirmedAt: null,
      }));
      setScreen('overview');
    })();
  };

  const completeGrading = (result: GradingResultSnapshot, answers: GradingAnswerSnapshot[]) => {
    const ts = new Date().toISOString();
    void persist((prev) => ({
      ...prev,
      grading: {
        status: 'pending_review',
        result,
        answers,
        updatedAt: ts,
        reviewSubmittedAt: ts,
      },
      confirmedAt: null,
    }));
    setScreen('gradingPending');
  };

  const saveUserProfile = (data: UserProfileFormData, visibility: CommunityVisibilitySettings) => {
    void persist((prev) => ({
      ...prev,
      userProfile: {
        data,
        visibility,
        updatedAt: new Date().toISOString(),
      },
    }));
  };

  const navHidden =
    screen === 'position'
    || screen === 'grading'
    || screen === 'share'
    || screen === 'positionPending'
    || screen === 'gradingPending';

  const openQuestionnaire = () => {
    setScreen(resolveRequestedScreen('share', positionLocked, gradingLocked));
  };

  const navItems: { id: Screen; label: string; locked?: boolean }[] = [
    { id: 'overview', label: 'Обзор' },
    { id: 'profile', label: 'Профиль' },
    { id: 'share', label: 'Анкета', locked: positionLocked && gradingLocked },
    {
      id: 'marketData',
      label: 'Рыночные данные',
      locked: progress.completed < 2,
    },
  ];

  const gradingNotSubmitted = !isQuestionnaireSubmitted(state.grading.status);
  const canOpenPosition = !positionLocked;
  const canOpenGrading = !gradingLocked;

  const positionFormKey = state.position.updatedAt ?? 'position-draft';

  return (
    <div className="dcf-mp">
      {saveError && (
        <p className="dcf-mp-error" role="alert" style={{ marginBottom: '0.75rem' }}>
          {saveError}
        </p>
      )}
      {!navHidden && (
        <nav className="dcf-mp-nav" aria-label="Разделы профиля рынка">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`dcf-mp-nav__btn ${screen === item.id ? 'dcf-mp-nav__btn--active' : ''} ${item.locked ? 'dcf-mp-nav__btn--locked' : ''}`}
              disabled={item.locked}
              onClick={() => {
                if (item.id === 'share') {
                  openQuestionnaire();
                  return;
                }
                navigateTo(item.id);
              }}
            >
              {item.label}
              {item.locked ? (
                <i className="fas fa-lock dcf-mp-nav__lock" aria-hidden="true" />
              ) : null}
            </button>
          ))}
        </nav>
      )}

      {screen === 'overview' && (
        <ProfileOverview
          state={state}
          onOpenProfile={() => navigateTo('profile')}
          onOpenShare={openQuestionnaire}
          onOpenPosition={() => navigateTo('position')}
          onOpenGrading={() => navigateTo('grading')}
          onOpenMarketData={() => navigateTo('marketData')}
          onOpenPositionPending={() => navigateTo('positionPending')}
          onOpenGradingPending={() => navigateTo('gradingPending')}
        />
      )}

      {screen === 'profile' && (
        <ProfileScreen profile={state.userProfile} onSave={saveUserProfile} />
      )}

      {screen === 'share' && canOpenPosition && canOpenGrading && (
        <CombinedShareQuestionnaire
          key={positionFormKey}
          positionInitial={state.position.data}
          onComplete={completeShare}
          onBack={() => setScreen('overview')}
        />
      )}

      {screen === 'position' && canOpenPosition && (
        <PositionQuestionnaire
          key={positionFormKey}
          initial={state.position.data}
          onComplete={completePosition}
          onBack={() => setScreen('overview')}
        />
      )}

      {screen === 'grading' && canOpenGrading && (
        <GradingQuestionnaire
          onComplete={completeGrading}
          onBack={() => setScreen('overview')}
        />
      )}

      {screen === 'positionPending' && state.position.reviewSubmittedAt && (
        <QuestionnairePendingReview
          kind="position"
          reviewSubmittedAt={state.position.reviewSubmittedAt}
          onBack={() => navigateTo('overview')}
          onContinue={gradingNotSubmitted && canOpenGrading ? () => navigateTo('grading') : undefined}
          continueLabel={gradingNotSubmitted ? 'Пройти грейдинг' : undefined}
        />
      )}

      {screen === 'gradingPending' && state.grading.reviewSubmittedAt && (
        <QuestionnairePendingReview
          kind="grading"
          reviewSubmittedAt={state.grading.reviewSubmittedAt}
          onBack={() => navigateTo('overview')}
        />
      )}

      {screen === 'marketData' && (
        <MarketDataTab
          state={state}
          onGoOverview={() => navigateTo('overview')}
          onOpenShare={canOpenPosition && canOpenGrading ? openQuestionnaire : undefined}
          onOpenPosition={canOpenPosition ? () => navigateTo('position') : undefined}
          onOpenGrading={canOpenGrading ? () => navigateTo('grading') : undefined}
        />
      )}
    </div>
  );
}
